import { useState, useEffect, useCallback } from "react";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { PandaDocConfig, PandaDocFolder } from "@/types";

// ==================== TYPES ====================

export type { PandaDocConfig };

export interface PandaDocConfigUpdate {
  isolation_mode?: string;
  template_folder_uuid?: string;
  document_folder_uuid?: string;
  credential_ref?: string;
  webhook_secret_ref?: string;
}

// ==================== HOOK ====================

interface UsePandaDocConfigOptions {
  partnerId: string;
  enabled?: boolean;
}

interface UsePandaDocConfigReturn {
  config: PandaDocConfig | null;
  templateFolders: PandaDocFolder[];
  documentFolders: PandaDocFolder[];
  isLoading: boolean;
  isUpdating: boolean;
  isLoadingTemplateFolders: boolean;
  isLoadingDocumentFolders: boolean;
  error: Error | null;
  updateError: Error | null;
  refetch: () => Promise<void>;
  updateConfig: (data: PandaDocConfigUpdate) => Promise<void>;
  fetchTemplateFolders: () => Promise<void>;
  fetchDocumentFolders: () => Promise<void>;
}

export const usePandaDocConfig = ({ partnerId, enabled = true }: UsePandaDocConfigOptions): UsePandaDocConfigReturn => {
  const [config, setConfig] = useState<PandaDocConfig | null>(null);
  const [templateFolders, setTemplateFolders] = useState<PandaDocFolder[]>([]);
  const [documentFolders, setDocumentFolders] = useState<PandaDocFolder[]>([]);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isLoadingTemplateFolders, setIsLoadingTemplateFolders] = useState(false);
  const [isLoadingDocumentFolders, setIsLoadingDocumentFolders] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  // Fetch PandaDoc config
  const fetchConfig = useCallback(async () => {
    if (!partnerId || !enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/partners/${partnerId}/configuration/pandadoc`);
      setConfig(json);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to fetch PandaDoc config");
      setError(error);
      console.error("[usePandaDocConfig] Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [partnerId, enabled]);

  // Update PandaDoc config
  const updateConfig = useCallback(
    async (data: PandaDocConfigUpdate) => {
      if (!partnerId) return;

      setIsUpdating(true);
      setUpdateError(null);

      try {
        const { json } = await httpClient(`${getPartnerAwareApiUrl()}/partners/${partnerId}/configuration/pandadoc`, {
          method: "PATCH",
          body: JSON.stringify(data),
        });
        setConfig(json.data ?? json);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to update PandaDoc config");
        setUpdateError(error);
        console.error("[usePandaDocConfig] Update error:", error);
        throw error;
      } finally {
        setIsUpdating(false);
      }
    },
    [partnerId]
  );

  // Fetch template folders
  const fetchTemplateFolders = useCallback(async () => {
    if (!partnerId) return;

    setIsLoadingTemplateFolders(true);

    try {
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/partners/${partnerId}/pandadoc/folders/templates`);
      setTemplateFolders(json.folders || []);
    } catch (err) {
      console.error("[usePandaDocConfig] Template folders error:", err);
    } finally {
      setIsLoadingTemplateFolders(false);
    }
  }, [partnerId]);

  // Fetch document folders
  const fetchDocumentFolders = useCallback(async () => {
    if (!partnerId) return;

    setIsLoadingDocumentFolders(true);

    try {
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/partners/${partnerId}/pandadoc/folders/documents`);
      setDocumentFolders(json.folders || []);
    } catch (err) {
      console.error("[usePandaDocConfig] Document folders error:", err);
    } finally {
      setIsLoadingDocumentFolders(false);
    }
  }, [partnerId]);

  // Initial fetch
  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  return {
    config,
    templateFolders,
    documentFolders,
    isLoading,
    isUpdating,
    isLoadingTemplateFolders,
    isLoadingDocumentFolders,
    error,
    updateError,
    refetch: fetchConfig,
    updateConfig,
    fetchTemplateFolders,
    fetchDocumentFolders,
  };
};
